import React, { useState, useEffect, useRef } from 'react';
import { useScreenState } from '../lib/screenState';
import { apiClient, downloadFile } from '../api';
import { browserZone, dayHeading, dayKey, dayRangeAsInstants, formatIsoDay, formatTime, isoDay } from '../lib/dates';
import { plural, thousands } from '../lib/text';
import { ChevronIcon, ClientNames, CloseIcon, inputClasses, secondaryButton, smallButton, smallNeutral, tableHeadCell } from '../components/ui';

// Los filtros viven en el estado de la pantalla bajo esta clave: "Historial", en Updates y en Clientes, los deja
// puestos antes de abrir Auditoría.
export const AUDIT_FILTERS_KEY = 'audit.filters';
export const NO_FILTERS = { q: '', version: '', action: '', user: '', client: '', update: '', from: '', to: '' };
const FILTER_LABELS = { q: 'Búsqueda', from: 'Desde', to: 'Hasta', version: 'Versión', action: 'Acción', user: 'Usuario', client: 'Cliente', update: 'Update' };
// En el filtro de Acción se puede elegir un grupo entero ("Todo lo de Updates"): viaja con este prefijo.
const GROUP = 'grupo:';
// Columnas y su ancho: fijo en todas menos el detalle, así nada se corre al pasar de página.
const COLUMNS = [['Hora', 'w-16'], ['Usuario', 'w-40'], ['Acción', 'w-52'], ['Versión', 'w-24'], ['Detalle', '']];
// Al abrir un registro se nombran hasta acá; uno que tocó más (igualar un cliente con miles) los tiene todos en el CSV.
const MAX_LISTED = 300;
const QUICK_RANGES = [['Hoy', 0, 0], ['Ayer', -1, -1], ['7 días', -6, 0]];
// Lo que se espera después de la última tecla del buscador antes de consultar.
const TYPING_WAIT_MS = 250;

// Lo que no hizo una persona queda con uno de estos usuarios: se nombra por lo que es y el tooltip lo explica.
const AUTOMATIC_USERS = {
    cron: ['automática', 'La sincronización programada, o lanzada desde la consola del servidor.'],
    sistema: ['sistema (al arrancar)', 'Lo que la aplicación corrige sola al arrancar.'],
    consola: ['consola del servidor', 'Un comando de administración corrido en el servidor.'],
};
const userLabel = (user) => AUTOMATIC_USERS[user]?.[0] || user;

const filterValue = (key, value) => (
    key === 'from' || key === 'to' ? formatIsoDay(value)
        : key === 'user' ? userLabel(value)
        : key === 'action' && value.startsWith(GROUP) ? `todo lo de ${value.slice(GROUP.length)}`
        : value);

// Lo que se le manda al servidor: los filtros con algo y las fechas como instantes del día de quien mira.
const queryParams = ({ from, to, action, ...rest }) => ({
    ...Object.fromEntries(Object.entries(rest).filter(([, value]) => value)),
    ...(action.startsWith(GROUP) ? { action_group: action.slice(GROUP.length) } : action ? { action } : {}),
    ...dayRangeAsInstants(from, to),
});

const sameNames = (a, b) => a.length === b.length && a.every(name => b.includes(name));

// El update en letra de nombre de archivo y oscura: es lo que se busca con la vista en la fila.
const UpdateName = ({ name }) => <span className="font-mono text-[13px] font-medium text-slate-800">{name}</span>;
// Las flechas del paginado.
const PageArrow = ({ back }) => <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block"><path d={back ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} /></svg>;
const Arrow = () => <span aria-hidden="true" className="text-slate-400"> → </span>;

// Lo que dice una fila, sin abrirla. Un registro de un solo update muestra el update, a quién fue y lo que agregue
// su resumen; uno de varios, el resumen ("18 updates en 5 clientes."); el que no guarda datos, su frase entera.
// Varios registros hechos de una vez van juntos: el update si es el mismo en todos, o cuántos fueron.
const rowText = (row) => {
    const grouped = row.entries.length > 1;
    const clients = row.clients_total > 0 && <><Arrow /><ClientNames names={row.clients} total={row.clients_total} prefix="" /></>;
    if (row.update && (grouped || row.summary !== null)) {
        return { plain: `${row.update} ${row.summary || ''}`, node: <><UpdateName name={row.update} />{clients}{row.summary && <span> · {row.summary}</span>}</> };
    }
    if (grouped) {
        const what = row.updates_total > 1 ? plural(row.updates_total, 'update', 'updates') : plural(row.entries.length, 'registro', 'registros');
        return { plain: what, node: <><span className="font-medium text-slate-800">{what}</span>{clients}</> };
    }
    const text = row.summary || row.details || '';
    return { plain: text, node: text };
};

// Hay más para ver que lo que entra en la fila: varios registros juntos, varios updates, prerrequisitos que se
// publicaron de paso (a quién fue cada uno), muchos clientes o una frase larga.
const hasMore = (row, plain) => row.entries.length > 1 || row.updates_total > 1 || row.also_total > 0 || row.clients_total > 5 || plain.length > 70;

// Un nombre de la lista de un registro abierto: al tocarlo, Auditoría queda filtrada por ese update o ese cliente.
const FilterLink = ({ kind, name, onFilter, mono }) => (
    <button type="button" onClick={() => onFilter(kind, name)} title={`Ver sólo los registros de ${name}`} aria-label={`Filtrar por ${name}`}
        className={`${mono ? 'font-mono text-[13px]' : ''} text-slate-800 hover:text-zafiro-700 hover:underline`}>{name}</button>
);

const Listed = ({ title, names, total, render }) => (
    <div className="flex gap-3">
        <span className="flex-shrink-0 w-28 text-xs font-semibold text-slate-500 uppercase tracking-wide pt-0.5">{title} ({thousands(total)})</span>
        <span className="min-w-0 break-words">
            {names.slice(0, MAX_LISTED).map((name, i) => <React.Fragment key={name}>{i > 0 && <span className="text-slate-300"> · </span>}{render(name)}</React.Fragment>)}
            {total > MAX_LISTED && <span className="text-slate-500"> … y {thousands(total - MAX_LISTED)} más, que están en el CSV.</span>}
        </span>
    </div>
);

// Un registro abierto: la lista completa de updates y clientes que tocó, que se pide recién acá. La frase entera se
// muestra cuando es lo único que hay (un registro sin datos) o cuando la fila no la dijo; si la fila ya dio el
// resumen y abajo está la lista, repetir la frase sería leer los mismos nombres dos veces.
const EntryDetail = ({ id, onFilter }) => {
    // undefined mientras se pide y null si no se pudo.
    const [detail, setDetail] = useState(undefined);
    useEffect(() => {
        let current = true;
        apiClient.get(`/api/audit-logs/${id}`).then(res => { if (current) setDetail(res.data); }).catch(() => { if (current) setDetail(null); });
        return () => { current = false; };
    }, [id]);
    if (detail === undefined) return <p className="text-slate-500">Cargando…</p>;
    if (detail === null) return <p role="alert" className="font-medium text-red-700">No se pudo cargar el detalle.</p>;
    const updates = detail.updates.map(update => update.name);
    // Si cada update fue a clientes distintos (un prerrequisito que no le llegó a todos), se dice a quién fue cada uno.
    const perUpdate = detail.updates.length > 1 && detail.updates.some(update => !sameNames(update.clients, detail.clients));
    const clientsOf = Object.fromEntries(detail.updates.map(update => [update.name, update.clients]));
    // Lo que se publicó de paso, por ser prerrequisito del update del que trata el registro, se dice al lado.
    const secondary = new Set(detail.updates.filter(update => update.secondary).map(update => update.name));
    const listed = updates.length > 0 || detail.clients.length > 0;
    return (
        <div className="space-y-1.5">
            {!(listed && detail.summary !== null) && <p className="break-words text-slate-700">{detail.details}</p>}
            {updates.length > 0 && <Listed title="Updates" names={updates} total={updates.length} render={name => (
                <><FilterLink kind="update" name={name} onFilter={onFilter} mono />{secondary.has(name) && <span className="text-slate-500"> (prerrequisito)</span>}{perUpdate && clientsOf[name].length > 0 && <span className="text-slate-500"> → {clientsOf[name].join(', ')}</span>}</>
            )} />}
            {detail.clients.length > 0 && <Listed title="Clientes" names={detail.clients} total={detail.clients.length} render={name => <FilterLink kind="client" name={name} onFilter={onFilter} />} />}
        </div>
    );
};

// Varios registros hechos de una vez, abiertos: cada uno con su versión, su update y a quién fue.
const GroupDetail = ({ entries, onFilter }) => (
    <ul className="space-y-1">
        {entries.map(entry => (
            <li key={entry.id} className="flex gap-3">
                <span className="flex-shrink-0 w-28 font-mono text-xs text-slate-500 pt-0.5">{entry.version || '—'}</span>
                <span className="min-w-0 break-words">
                    {entry.update && entry.summary !== null
                        ? <><FilterLink kind="update" name={entry.update} onFilter={onFilter} mono />{entry.clients.length > 0 && <><Arrow />{entry.clients.map((name, i) => <React.Fragment key={name}>{i > 0 && ', '}<FilterLink kind="client" name={name} onFilter={onFilter} /></React.Fragment>)}{entry.clients_total > entry.clients.length && ` y ${entry.clients_total - entry.clients.length} más`}</>}{entry.summary && <span> · {entry.summary}</span>}</>
                        : entry.details}
                </span>
            </li>
        ))}
    </ul>
);

const LogRow = ({ row, open, onToggle, onFilter }) => {
    const { plain, node } = rowText(row);
    const expandable = hasMore(row, plain);
    const automatic = AUTOMATIC_USERS[row.user];
    const versions = row.versions || [];
    return (
        <>
            <tr className={`border-b border-slate-100 ${open ? 'bg-slate-50' : ''}`}>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-[13px] text-slate-700">{formatTime(row.timestamp)}</td>
                <td className="px-3 py-2 truncate" title={automatic?.[1] || row.user}>{automatic ? <span className="text-slate-500">{automatic[0]}</span> : row.user}</td>
                {/* El nombre de la acción se lee entero: el más largo pasa a dos líneas, no se recorta. */}
                <td className="px-3 py-2 font-medium leading-snug break-words">{row.action}</td>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                    {row.version || (versions.length > 1
                        ? <span title={versions.join(', ')} className="font-sans underline decoration-dotted decoration-slate-400 underline-offset-2 cursor-help">{versions.length} versiones</span>
                        : <span title="No es de una versión." className="text-slate-400">—</span>)}
                </td>
                <td className="px-3 py-2 text-slate-600">
                    <span className="flex items-baseline gap-3">
                        <span className="flex-grow min-w-0 truncate" title={expandable ? undefined : plain}>{node}</span>
                        {expandable && <button type="button" onClick={onToggle} aria-expanded={open} aria-label={`${open ? 'ocultar' : 'ver'} detalle: ${row.action} de las ${formatTime(row.timestamp)}`}
                            className="flex-shrink-0 text-xs font-semibold text-zafiro-600 hover:underline whitespace-nowrap">{open ? 'ocultar detalle' : 'ver detalle'}</button>}
                    </span>
                </td>
            </tr>
            {open && (
                <tr className="border-b border-slate-100 bg-slate-50">
                    <td />
                    <td colSpan={COLUMNS.length - 1} className="px-3 pb-3 text-sm text-slate-600">
                        {row.entries.length > 1 ? <GroupDetail entries={row.entries} onFilter={onFilter} /> : <EntryDetail id={row.id} onFilter={onFilter} />}
                    </td>
                </tr>
            )}
        </>
    );
};

// Con `updatesOnly` (quien no es administrador) el servidor entrega sólo los registros de updates, y acá se avisa.
export default function AuditLogPanel({ versions, addToast, updatesOnly = false }) {
    // undefined hasta la primera respuesta; después, lo último que se cargó.
    const [data, setData] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [loadedAt, setLoadedAt] = useState(null);
    // La página no se recuerda: al volver a la sección se ve la primera, donde está lo más reciente.
    const [page, setPage] = useState(1);
    const [refreshes, setRefreshes] = useState(0);
    const [storedFilters, setFilters] = useScreenState(AUDIT_FILTERS_KEY, NO_FILTERS);
    const filters = { ...NO_FILTERS, ...storedFilters };
    const [filtersOpen, setFiltersOpen] = useScreenState('audit.filtersOpen', false);
    const [lists, setLists] = useState({ actions: [], users: [], clients: [] });
    const [openRows, setOpenRows] = useState([]);
    const tableBox = useRef(null);
    const previousPage = useRef(null);
    const nextPage = useRef(null);

    // Las listas de los filtros llegan en un solo pedido, y se vuelven a pedir al actualizar: lo nuevo puede traer
    // una acción, un usuario o un cliente que todavía no estaba.
    useEffect(() => {
        apiClient.get('/api/audit-filters').then(res => setLists(res.data)).catch(() => addToast('No se pudieron cargar las listas de los filtros.', 'error'));
    }, [addToast, refreshes]);

    const invertedRange = Boolean(filters.from && filters.to && filters.from > filters.to);
    const filtersKey = JSON.stringify(filters);

    // Una pequeña espera, sólo mientras se escribe en el buscador, para no consultar en cada tecla; al entrar, al
    // cambiar de página o de filtro se consulta ya. Una respuesta que llega cuando ya se pidió otra cosa (se siguió
    // escribiendo, se cambió de página) no se usa: `current` deja de valer al limpiar.
    const typing = useRef(false);
    useEffect(() => {
        if (invertedRange) { setLoading(false); return undefined; }
        let current = true;
        const wait = typing.current ? TYPING_WAIT_MS : 0;
        typing.current = false;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.get('/api/audit-logs', { params: { page, ...queryParams(JSON.parse(filtersKey)) } });
                if (!current) return;
                setData(res.data); setFailed(false); setLoadedAt(new Date()); setOpenRows([]);
                if (tableBox.current) tableBox.current.scrollTop = 0;
            } catch (e) {
                if (current) setFailed(true);
            } finally { if (current) setLoading(false); }
        }, wait);
        return () => { current = false; clearTimeout(timer); };
    }, [page, filtersKey, refreshes, invertedRange]);

    const change = (patch) => { setFilters(f => ({ ...NO_FILTERS, ...f, ...patch })); setPage(1); };
    const setFilter = (key) => (e) => change({ [key]: e.target.value });
    const clearFilters = () => { setFilters(NO_FILTERS); setPage(1); };
    const activeFilters = Object.keys(FILTER_LABELS).filter(key => filters[key]);
    const filtered = activeFilters.length > 0;
    // Los filtros que están detrás de "Filtros": cuántos hay puestos se ve en el botón aunque esté cerrado.
    const tucked = ['version', 'action', 'user', 'client'].filter(key => filters[key]).length;
    const activeRange = QUICK_RANGES.find(([, from, to]) => filters.from === isoDay(from) && filters.to === isoDay(to))?.[0];

    const exportCsv = async () => {
        try {
            const res = await downloadFile('/api/audit-logs.csv', `auditoria_${isoDay()}.csv`, { ...queryParams(filters), ...browserZone() });
            const records = Number(res?.headers?.['x-audit-records']);
            const rows = Number(res?.headers?.['x-audit-rows']);
            if (Number.isNaN(records)) return;
            const said = records === 1 ? 'Se exportó 1 registro' : `Se exportaron ${thousands(records)} registros`;
            const spread = rows > records ? `, en ${thousands(rows)} filas: una por update y por cliente` : '';
            if (res.headers['x-audit-truncated'] === '1') addToast(`Se exportaron los ${thousands(records)} registros más recientes${spread}, y quedaron más sin exportar. Para el resto, acotá las fechas.`, 'warning');
            else addToast(`${said}${spread}.`, 'success');
        } catch (e) { addToast('No se pudo exportar.', 'error'); }
    };

    const usable = data && !failed && !invertedRange;
    const logs = usable ? data.logs : [];
    const total = data?.total ?? 0;
    const totalPages = data?.total_pages || 1;
    const first = (page - 1) * (data?.per_page || 20) + 1;
    // Una flecha que llega a su extremo se apaga: el foco pasa a la otra, para seguir con el teclado.
    const turnPage = (to) => {
        if (to <= 1) nextPage.current?.focus();
        if (to >= totalPages) previousPage.current?.focus();
        setPage(to);
    };
    // Qué decir cuando la tabla no tiene filas: cada situación tiene su frase.
    const emptyText = invertedRange ? 'Corregí las fechas para ver los registros.'
        : data === undefined ? 'Cargando…'
        : filtered ? 'Ningún registro coincide con estos filtros.'
        : updatesOnly ? 'Todavía no hay registros de updates.' : 'Todavía no hay registros.';
    // Las opciones de un filtro, con lo que ya está elegido aunque no figure en la lista (llegó desde "Historial").
    const withCurrent = (names, current) => (current && !names.includes(current) ? [current, ...names] : names);
    const now = new Date();

    return (
        <div className="h-full flex flex-col">
            <div className="flex-shrink-0 px-6 py-3 bg-white border-b border-slate-900/10">
                <div className="flex flex-wrap items-center gap-3">
                    <input type="search" aria-label="Buscar en el registro" value={filters.q} onChange={(e) => { typing.current = true; setFilter('q')(e); }} placeholder="Buscar update, cliente o texto…" className={`${inputClasses} flex-grow min-w-[12rem]`} />
                    <div role="group" aria-label="Fechas" className="relative flex items-center gap-2 text-sm text-slate-500">
                        {QUICK_RANGES.map(([label, from, to]) => (
                            <button key={label} type="button" aria-pressed={activeRange === label} onClick={() => change(activeRange === label ? { from: '', to: '' } : { from: isoDay(from), to: isoDay(to) })}
                                className={`${smallButton} py-2 ${activeRange === label ? 'border-zafiro-600 bg-zafiro-50 text-zafiro-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{label}</button>
                        ))}
                        <input type="date" aria-label="Desde" aria-invalid={invertedRange} value={filters.from} onChange={setFilter('from')} className={`${inputClasses} px-2 ${invertedRange ? 'border-red-400' : ''}`} />
                        <span>a</span>
                        <input type="date" aria-label="Hasta" aria-invalid={invertedRange} value={filters.to} onChange={setFilter('to')} className={`${inputClasses} px-2 ${invertedRange ? 'border-red-400' : ''}`} />
                        {invertedRange && <span role="alert" className="absolute right-0 top-full mt-0.5 text-xs font-medium text-red-700 whitespace-nowrap">«Desde» es posterior a «Hasta».</span>}
                    </div>
                    <button type="button" onClick={() => setFiltersOpen(open => !open)} aria-expanded={filtersOpen} className={`${secondaryButton} flex items-center gap-1.5 px-3`}>Filtros{tucked > 0 && <span className="px-1.5 rounded-full bg-zafiro-600 text-white text-xs">{tucked}</span>}<ChevronIcon open={filtersOpen} /></button>
                    <button type="button" onClick={() => setRefreshes(n => n + 1)} title="Vuelve a pedir el registro: lo hecho mientras la pantalla está abierta no aparece solo." className={`${secondaryButton} px-3`}>Actualizar</button>
                    <button type="button" onClick={exportCsv} title="Descarga los registros que se ven con estos filtros (hasta 50.000)." className={`${secondaryButton} px-3`}>Exportar CSV</button>
                </div>
                {filtersOpen && (
                    <div role="group" aria-label="Filtros" className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-500">
                        <label className="flex items-center gap-1.5">Versión <select aria-label="Versión" value={filters.version} onChange={setFilter('version')} className={`${inputClasses} font-mono`}><option value="">Todas las versiones</option>{withCurrent(versions.map(v => v.id), filters.version).map(v => <option key={v} value={v}>{v}</option>)}</select></label>
                        <label className="flex items-center gap-1.5">Acción <select aria-label="Acción" value={filters.action} onChange={setFilter('action')} className={`${inputClasses} max-w-[15rem]`}>
                            <option value="">Todas las acciones</option>
                            {lists.actions.length === 1
                                ? lists.actions[0].actions.map(a => <option key={a} value={a}>{a}</option>)
                                : lists.actions.map(({ group, actions }) => (
                                    <optgroup key={group} label={group}>
                                        {actions.length > 1 && <option value={`${GROUP}${group}`}>Todo lo de {group}</option>}
                                        {actions.map(a => <option key={a} value={a}>{a}</option>)}
                                    </optgroup>
                                ))}
                        </select></label>
                        <label className="flex items-center gap-1.5">Usuario <select aria-label="Usuario" value={filters.user} onChange={setFilter('user')} className={inputClasses}><option value="">Todos los usuarios</option>{withCurrent(lists.users, filters.user).map(u => <option key={u} value={u}>{userLabel(u)}</option>)}</select></label>
                        <label className="flex items-center gap-1.5">Cliente <select aria-label="Cliente" value={filters.client} onChange={setFilter('client')} className={`${inputClasses} font-mono`}><option value="">Todos los clientes</option>{withCurrent(lists.clients, filters.client).map(c => <option key={c} value={c}>{c}</option>)}</select></label>
                    </div>
                )}
            </div>
            {updatesOnly && <p role="note" className="flex-shrink-0 mx-6 mt-3 text-sm text-slate-600">Sólo registros de updates. El resto lo ve un administrador.</p>}
            {filtered && (
                <div role="status" className="flex-shrink-0 flex flex-wrap items-center gap-2 mx-6 mt-3 px-4 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
                    <span className="font-semibold">{usable ? `${thousands(total)} de ${thousands(data.total_all)} ${data.total_all === 1 ? 'registro' : 'registros'}` : 'Filtrado por'}</span>
                    {activeFilters.map(key => (
                        <span key={key} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-white border border-amber-200">
                            <span>{FILTER_LABELS[key]}: <span className={key === 'update' ? 'font-mono text-[13px]' : 'font-medium'}>{filterValue(key, filters[key])}</span></span>
                            <button type="button" onClick={() => change({ [key]: '' })} aria-label={`Quitar el filtro ${FILTER_LABELS[key]}`} title="Quitar este filtro" className="rounded text-amber-700 hover:bg-amber-100"><CloseIcon /></button>
                        </span>
                    ))}
                    <button type="button" onClick={clearFilters} className={`${smallNeutral} bg-white ml-auto`}>Limpiar filtros</button>
                </div>
            )}
            <div ref={tableBox} aria-busy={loading} className="flex-grow min-h-0 overflow-auto mx-6 mt-3 bg-white border border-slate-900/10 rounded-xl shadow-sm">
                <table className="w-full table-fixed text-sm">
                    <colgroup>{COLUMNS.map(([name, width]) => <col key={name} className={width} />)}</colgroup>
                    <thead className="sticky top-0 z-10 bg-slate-50"><tr>{COLUMNS.map(([name]) => <th key={name} className={`${tableHeadCell} border-b border-slate-200`}>{name}</th>)}</tr></thead>
                    <tbody className={loading && logs.length > 0 ? 'opacity-60' : ''}>
                        {logs.map((row, i) => {
                            const heading = (i === 0 || dayKey(logs[i - 1].timestamp) !== dayKey(row.timestamp)) && dayHeading(row.timestamp, now);
                            return (
                                <React.Fragment key={row.id}>
                                    {heading && <tr><th colSpan={COLUMNS.length} scope="colgroup" className="px-3 pt-3 pb-1.5 text-left text-xs font-semibold text-slate-600 bg-white border-b border-slate-200">{heading.relative && <><span className="uppercase tracking-wide text-slate-900">{heading.relative}</span> · </>}{heading.text}</th></tr>}
                                    <LogRow row={row} open={openRows.includes(row.id)} onFilter={(kind, name) => change({ [kind]: name })}
                                        onToggle={() => setOpenRows(open => (open.includes(row.id) ? open.filter(id => id !== row.id) : [...open, row.id]))} />
                                </React.Fragment>
                            );
                        })}
                        {failed && !invertedRange && (
                            <tr><td colSpan={COLUMNS.length} className="px-3 py-6">
                                <span role="alert" className="font-medium text-red-700">No se pudo cargar el registro.</span>
                                <button type="button" onClick={() => setRefreshes(n => n + 1)} className={`${smallNeutral} ml-3`}>Reintentar</button>
                            </td></tr>
                        )}
                        {!failed && logs.length === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="px-3 py-6 text-slate-500">
                                {emptyText}
                                {filtered && data !== undefined && !invertedRange && <button type="button" onClick={clearFilters} className={`${smallNeutral} ml-3`}>Limpiar filtros</button>}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-3 text-sm text-slate-600">
                <span className="min-w-0">
                    {logs.length > 0 && `${first} a ${first + logs.length - 1} de ${thousands(total)} ${total === 1 ? 'registro' : 'registros'}`}
                    {loadedAt && <span className="text-slate-400">{logs.length > 0 && ' · '}Actualizado a las {formatTime(loadedAt)}</span>}
                </span>
                {/* Paginado de siempre: flecha, número de página, flecha. La primera página es la de lo más reciente. */}
                <nav aria-label="Páginas" className="flex-shrink-0 flex items-center gap-2">
                    <button ref={previousPage} type="button" onClick={() => turnPage(page - 1)} disabled={page <= 1} aria-label="Página anterior" title="Página anterior (más recientes)" className={`${secondaryButton} px-2.5`}><PageArrow back /></button>
                    <span aria-current="page" className="min-w-[7rem] text-center font-medium text-slate-900 whitespace-nowrap">Página {page} de {totalPages}</span>
                    <button ref={nextPage} type="button" onClick={() => turnPage(page + 1)} disabled={page >= totalPages} aria-label="Página siguiente" title="Página siguiente (más antiguos)" className={`${secondaryButton} px-2.5`}><PageArrow /></button>
                </nav>
            </div>
        </div>
    );
}
